import React, { useState, useEffect } from "react";
import TopBar from "../../components/TopBar";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { FaSearch, FaChevronDown } from "react-icons/fa";
import { toast } from "react-toastify";
import supabase from "../../supabase";

const statusColors = {
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  shipped: "bg-blue-100 text-blue-800",
  waiting: "bg-yellow-100 text-yellow-800",
  processing: "bg-orange-100 text-orange-800"
};

async function loadOrders(status) {
  let q = supabase.from("payments").select("id, created_at, customer_name, amount, status, provider").order("created_at", { ascending: false });
  if (status && status !== "all") q = q.eq("status", status);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((row) => ({
    id: row.id,
    order_status: row.status?.toLowerCase?.() || "processing",
    created_at: row.created_at,
    customer_name: row.customer_name || "Customer",
    product: "—",
    weight: 0,
    total_amount: Number(row.amount) || 0,
    payment_method: row.provider || "—",
  }));
}

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("waiting");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const rows = await loadOrders(status);
        setOrders(rows);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        toast.error("Failed to load orders");
      }
    })();
  }, [status]);

  const filteredOrders = orders.filter(order =>
    order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.id.toString().includes(searchTerm) ||
    order.product.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page">
      <TopBar />
      <div className="p-6">
        <div className="orders-header mb-6">
          <h1 className="orders-title text-2xl font-bold mb-2">Orders</h1>
          <div className="orders-toolbar flex items-center gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-3 border rounded-lg w-full text-center"
              />
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <div className="relative">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="border rounded-lg pl-4 pr-10 py-3 appearance-none bg-white"
              >
                <option value="all">All</option>
                <option value="waiting">Waiting</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden table-container">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gold Weight (g)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr
                  key={order.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/order/${order.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">#{order.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{moment(order.created_at).format("MMM DD, YYYY")}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.customer_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.product}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.weight}g</td>
                  <td className="px-6 py-4 whitespace-nowrap">₹{order.total_amount.toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.payment_method}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs ${statusColors[order.order_status] || "bg-gray-100 text-gray-800"}`}>
                      {order.order_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Orders;
